import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import "./AutomatedOffersForm.scss";
import brand from "../../pages/AutomatedOffers/brands";
import EstimatePanel from "./enroll/EstimatePanel";
import PackageChoices from "./enroll/PackageChoices";
import ProviderSelect from "./enroll/ProviderSelect";
import WebsiteChoices from "./enroll/WebsiteChoices";
import { EMAIL_PATTERN, formatMoney, isValidUrl, US_STATES } from "./formUtils";

const {
	authorizationText,
	brands: BRANDS,
	jotformFields: JOTFORM_FIELDS,
	jotformId: FORM_ID,
	labels,
	packageOptions,
	providerHint,
	providerOptions,
} = brand.form;

const findPackageById = (id) =>
	packageOptions.find((p) => p.id === id) || packageOptions[0];

/** Field ids double as error keys, so the first invalid field can be focused. */
const fieldId = (key) => `ao-${key}`;
const urlKey = (site) => `url-${site.replace(/\s+/g, "-").toLowerCase()}`;

const EMPTY_FIELDS = {
	dealer: "",
	dealerCode: "",
	city: "",
	state: "",
	docFee: "",
	accessories: "",
	contactName: "",
	contactTitle: "",
	contactEmail: "",
	contactPhone: "",
};

/** Single-submit flow: one JotForm submission per enrollment, then a confirmation panel. */
function AutomatedOffersSubmitForm({ selection, onSelectionChange }) {
	const [fields, setFields] = useState(EMPTY_FIELDS);
	const [urls, setUrls] = useState(() =>
		Object.fromEntries(BRANDS.map((site) => [site, ""])),
	);
	const [provider, setProvider] = useState("");
	const [providerOther, setProviderOther] = useState("");
	const [authorized, setAuthorized] = useState(false);
	const [errors, setErrors] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(null);
	const [copied, setCopied] = useState(false);
	const doneRef = useRef(null);
	const formRef = useRef(null);

	const selectedPackage = findPackageById(selection.package);
	const total =
		selectedPackage.bothRate && selection.brands.length > 1
			? selectedPackage.bothRate
			: selectedPackage.rate * selection.brands.length;
	const ratePerSite = selection.brands.length ? total / selection.brands.length : selectedPackage.rate;

	const clearError = (key) =>
		setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));

	const setField = (key) => (e) => {
		setFields((prev) => ({ ...prev, [key]: e.target.value }));
		clearError(key);
	};

	const toggleBrand = (site) => {
		const brands = selection.brands.includes(site)
			? selection.brands.filter((b) => b !== site)
			: BRANDS.filter((b) => b === site || selection.brands.includes(b));
		onSelectionChange({ ...selection, brands });
		clearError("brand");
	};

	const validate = () => {
		// Same order as on screen, so the first invalid field gets focus.
		const next = {};
		if (!selection.brands.length) next.brand = "Select at least one website to enroll.";
		selection.brands.forEach((site) => {
			if (!isValidUrl(urls[site].trim()))
				next[urlKey(site)] = `Enter the full ${site} website address, starting with https://`;
		});
		if (!provider) next.provider = "Select your website provider.";
		if (!fields.dealer.trim()) next.dealer = "Enter your dealer name.";
		if (!fields.city.trim()) next.city = "Enter your city.";
		if (!fields.state) next.state = "Select your state.";
		if (!fields.contactName.trim()) next.contactName = "Enter the primary contact's name.";
		if (!EMAIL_PATTERN.test(fields.contactEmail.trim()))
			next.contactEmail = "Enter a valid email address.";
		if (!authorized) next.authorized = "Confirm you're authorized to enroll these websites.";

		setErrors(next);
		const firstInvalid = Object.keys(next)[0];
		if (firstInvalid === "brand")
			formRef.current?.querySelector('input[name="brand"]')?.focus();
		else if (firstInvalid) document.getElementById(fieldId(firstInvalid))?.focus();
		return !firstInvalid;
	};

	const collect = () => ({
		package: selectedPackage.label,
		ratePerSite: formatMoney(ratePerSite),
		websitesSelected: selection.brands.join(" + "),
		monthlyEstimate: formatMoney(total),
		bmwUrl: selection.brands.includes("BMW") ? urls.BMW.trim() : "",
		miniUrl: selection.brands.includes("MINI") ? urls.MINI.trim() : "",
		provider: provider === "Other" ? `Other: ${providerOther.trim()}` : provider,
		dealer: fields.dealer.trim(),
		dealerCode: fields.dealerCode.trim(),
		city: fields.city.trim(),
		state: fields.state,
		docFee: fields.docFee.trim(),
		accessories: fields.accessories.trim(),
		contactName: fields.contactName.trim(),
		contactTitle: fields.contactTitle.trim(),
		contactEmail: fields.contactEmail.trim(),
		contactPhone: fields.contactPhone.trim(),
		authorized: "Yes",
	});

	const summaryText = (r) =>
		[
			labels.summaryTitle,
			`Dealer: ${r.dealer}${r.dealerCode ? ` (${r.dealerCode})` : ""}, ${r.city}, ${r.state}`,
			`Websites: ${r.websitesSelected}`,
			r.bmwUrl && `BMW URL: ${r.bmwUrl}`,
			r.miniUrl && `MINI URL: ${r.miniUrl}`,
			`Provider: ${r.provider}`,
			`Package: ${r.package}`,
			`Estimate: ${r.ratePerSite} per website, ${r.monthlyEstimate} per month`,
			r.docFee && `Document fee: ${r.docFee}`,
			r.accessories && `Accessories: ${r.accessories}`,
			`Contact: ${r.contactName}${r.contactTitle ? `, ${r.contactTitle}` : ""}, ${r.contactEmail}${r.contactPhone ? `, ${r.contactPhone}` : ""}`,
		]
			.filter(Boolean)
			.join("\n");

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!validate()) return;
		const record = collect();
		setSubmitting(true);
		if (FORM_ID) {
			const data = new FormData();
			data.append("formID", FORM_ID);
			data.append("simple_spc", `${FORM_ID}-${FORM_ID}`);
			const [firstName, ...rest] = record.contactName.split(/\s+/);
			const payload = {
				...record,
				firstName,
				lastName: rest.join(" "),
				brand: record.websitesSelected,
				// JotForm's Document Fee is a number field.
				docFee: record.docFee.replace(/[^\d.]/g, ""),
				websiteUrl: [
					record.bmwUrl && `BMW: ${record.bmwUrl}`,
					record.miniUrl && `MINI: ${record.miniUrl}`,
				]
					.filter(Boolean)
					.join(" | "),
			};
			Object.entries(JOTFORM_FIELDS).forEach(([key, name]) =>
				data.append(name, payload[key] ?? ""),
			);
			try {
				await fetch(`https://submit.jotform.com/submit/${FORM_ID}`, {
					method: "POST",
					mode: "no-cors",
					keepalive: true,
					body: data,
				});
			} catch (err) {
				// no-cors responses are opaque; continue either way.
			}
		}
		setSubmitting(false);
		setSubmitted(record);
		setCopied(false);
		requestAnimationFrame(() =>
			doneRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
		);
	};

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(summaryText(submitted));
			setCopied(true);
		} catch (err) {
			// Clipboard unavailable: the summary stays visible to copy by hand.
		}
	};

	const handleEnrollAnother = () => {
		setFields(EMPTY_FIELDS);
		setUrls(Object.fromEntries(BRANDS.map((site) => [site, ""])));
		setProvider("");
		setProviderOther("");
		setAuthorized(false);
		setErrors({});
		setSubmitted(null);
	};

	const renderError = (key) =>
		errors[key] ? (
			<div className="err" id={`${fieldId(key)}-err`}>
				{errors[key]}
			</div>
		) : null;

	const textInput = (key, label, { required, type = "text", ...rest } = {}) => (
		<div className={`field${errors[key] ? " field--error" : ""}`}>
			<label className={`f${required ? " req" : ""}`} htmlFor={fieldId(key)}>
				{label}
			</label>
			<input
				type={type}
				id={fieldId(key)}
				value={fields[key]}
				onChange={setField(key)}
				aria-invalid={Boolean(errors[key])}
				aria-describedby={errors[key] ? `${fieldId(key)}-err` : undefined}
				{...rest}
			/>
			{renderError(key)}
		</div>
	);

	const estimate = (
		<EstimatePanel
			packageLabel={selectedPackage.label}
			websites={selection.brands.length ? selection.brands.join(" + ") : "None selected"}
			rate={formatMoney(ratePerSite)}
			total={formatMoney(total)}
			per="per month"
			fine="Estimate only. Third-party website provider fees, if any, are separate. Constellation confirms your final rate."
		/>
	);

	if (submitted) {
		return (
			<div className="form-grid">
				<div className="done" ref={doneRef} role="status">
					<h3>Enrollment received</h3>
					<p>
						Thank you. Constellation will confirm your package and reach out to{" "}
						{submitted.contactEmail} to schedule onboarding.
					</p>
					<pre>{summaryText(submitted)}</pre>
					<div className="done__actions">
						<button type="button" className="btn" onClick={handleCopy}>
							{copied ? "Copied" : "Copy details"}
						</button>
						<button type="button" className="btn ghost" onClick={handleEnrollAnother}>
							Enroll another dealer
						</button>
					</div>
				</div>
				{estimate}
			</div>
		);
	}

	return (
		<div className="form-grid">
			<form ref={formRef} noValidate onSubmit={handleSubmit}>
				{packageOptions.length > 1 && (
					<fieldset>
						<legend>Package</legend>
						<PackageChoices
							options={packageOptions}
							value={selection.package}
							onChange={(id) => onSelectionChange({ ...selection, package: id })}
						/>
					</fieldset>
				)}

				<fieldset>
					<legend>Websites to enroll</legend>
					<WebsiteChoices
						brands={BRANDS}
						selected={selection.brands}
						onToggle={toggleBrand}
						noun={labels.websiteNoun}
					/>
					{renderError("brand")}
					{selection.brands.length > 0 && (
						<div className="row ao-site-urls">
							{selection.brands.map((site) => {
								const key = urlKey(site);
								return (
									<div className={`field${errors[key] ? " field--error" : ""}`} key={site}>
										<label className="f req" htmlFor={fieldId(key)}>
											{site} website URL
										</label>
										<input
											type="url"
											id={fieldId(key)}
											value={urls[site]}
											placeholder={labels.urlPlaceholders[site]}
											onChange={(e) => {
												setUrls((prev) => ({ ...prev, [site]: e.target.value }));
												clearError(key);
											}}
											aria-invalid={Boolean(errors[key])}
										/>
										{renderError(key)}
									</div>
								);
							})}
						</div>
					)}
					<ProviderSelect
						id={fieldId("provider")}
						otherId={fieldId("providerOther")}
						options={providerOptions}
						value={provider}
						other={providerOther}
						hint={providerHint(provider)}
						error={errors.provider}
						onChange={(value) => {
							setProvider(value);
							clearError("provider");
						}}
						onOtherChange={setProviderOther}
					/>
				</fieldset>

				<fieldset>
					<legend>Dealer</legend>
					<div className="row">
						{textInput("dealer", "Dealer name", {
							required: true,
							autoComplete: "organization",
							placeholder: labels.dealerPlaceholder,
						})}
						{textInput("dealerCode", labels.code)}
						{textInput("city", "City", { required: true, autoComplete: "address-level2" })}
						<div className={`field${errors.state ? " field--error" : ""}`}>
							<label className="f req" htmlFor={fieldId("state")}>
								State
							</label>
							<select
								id={fieldId("state")}
								value={fields.state}
								onChange={setField("state")}
								autoComplete="address-level1"
								aria-invalid={Boolean(errors.state)}>
								<option value="">Select a state</option>
								{US_STATES.map((code) => (
									<option key={code}>{code}</option>
								))}
							</select>
							{fields.state === "CA" && <div className="hint">{labels.caHint}</div>}
							{renderError("state")}
						</div>
					</div>
				</fieldset>

				<fieldset>
					<legend>Offer inputs</legend>
					<div className="row">
						<div className="field">
							<label className="f" htmlFor={fieldId("docFee")}>
								Document fee
							</label>
							<input
								type="text"
								id={fieldId("docFee")}
								value={fields.docFee}
								onChange={setField("docFee")}
								inputMode="decimal"
								placeholder="$0.00"
							/>
							<div className="hint">You can confirm this during onboarding.</div>
						</div>
						{textInput("accessories", labels.accessories, {
							placeholder: "For example, tint and wheel locks, $895",
						})}
					</div>
				</fieldset>

				<fieldset>
					<legend>Primary contact</legend>
					<div className="row">
						{textInput("contactName", "Name", { required: true, autoComplete: "name" })}
						{textInput("contactTitle", "Title", {
							autoComplete: "organization-title",
							placeholder: "General Manager",
						})}
						{textInput("contactEmail", "Email", {
							required: true,
							type: "email",
							autoComplete: "email",
						})}
						{textInput("contactPhone", "Phone", { type: "tel", autoComplete: "tel" })}
					</div>
				</fieldset>

				<label className={`agree${errors.authorized ? " field--error" : ""}`}>
					<input
						type="checkbox"
						id={fieldId("authorized")}
						checked={authorized}
						aria-required="true"
						aria-invalid={Boolean(errors.authorized)}
						onChange={(e) => {
							setAuthorized(e.target.checked);
							clearError("authorized");
						}}
					/>
					<span>{authorizationText}</span>
				</label>
				{renderError("authorized")}

				<div className="ao-submit">
					<button className="btn" type="submit" disabled={submitting}>
						{submitting ? "Submitting…" : "Submit enrollment"}
					</button>
				</div>
			</form>

			{estimate}
		</div>
	);
}

AutomatedOffersSubmitForm.propTypes = {
	selection: PropTypes.shape({
		package: PropTypes.string.isRequired,
		brands: PropTypes.arrayOf(PropTypes.string).isRequired,
	}).isRequired,
	onSelectionChange: PropTypes.func.isRequired,
};

export default AutomatedOffersSubmitForm;
